import { useState } from "react";
import type { FormEvent } from "react";

const BROKER_JWT_ENDPOINT = "/admin/broker/jwt";

export function BrokerAdmin() {
  const [jwt, setJwt] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const email = String(form.get("email") ?? "").trim();
    setIsGenerating(true);
    setMessage(null);

    try {
      setJwt(await mockGenerateBrokerJwt(BROKER_JWT_ENDPOINT, email));
      setMessage("Broker JWT generated locally. It was not sent to the backend.");
    } catch (error) {
      setJwt(null);
      setMessage(error instanceof Error ? error.message : "Unable to generate JWT.");
    } finally {
      setIsGenerating(false);
    }
  }

  async function copyJwt() {
    if (!jwt) return;
    try {
      await navigator.clipboard.writeText(jwt);
      setMessage("JWT copied to the clipboard.");
    } catch {
      setMessage("Clipboard access was unavailable. Select and copy the JWT manually.");
    }
  }

  return (
    <section className="mx-auto max-w-4xl">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-mls-600">
        Local administration
      </p>
      <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">
        Broker Admin
      </h1>
      <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
        Provision a local development JWT for a broker session. The email is
        validated in this browser and is never transmitted or encoded in the token.
      </p>

      <form
        className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
        onSubmit={handleSubmit}
      >
        <label className="block text-sm font-medium text-slate-700">
          Broker email
          <input
            className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 outline-none transition focus:border-mls-600 focus:ring-2 focus:ring-mls-600/20"
            name="email"
            type="email"
            autoComplete="off"
            required
          />
        </label>
        <button
          className="mt-5 rounded-lg bg-mls-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-mls-600 disabled:opacity-60"
          type="submit"
          disabled={isGenerating}
        >
          {isGenerating ? "Generating…" : "Generate JWT"}
        </button>
      </form>

      {jwt ? (
        <section className="mt-6 rounded-2xl border border-emerald-200 bg-white p-6 shadow-sm">
          <h2 className="font-semibold text-slate-950">Generated JWT</h2>
          <textarea
            aria-label="Generated broker JWT"
            className="mt-4 h-32 w-full resize-none rounded-xl bg-slate-950 p-4 font-mono text-xs text-emerald-200"
            readOnly
            value={jwt}
          />
          <button
            className="mt-4 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            type="button"
            onClick={copyJwt}
          >
            Copy JWT
          </button>
        </section>
      ) : null}

      {message ? <p className="mt-4 text-sm text-slate-600" role="status">{message}</p> : null}
    </section>
  );
}

async function mockGenerateBrokerJwt(endpoint: string, email: string): Promise<string> {
  await new Promise((resolve) => window.setTimeout(resolve, 300));
  if (endpoint !== BROKER_JWT_ENDPOINT || !email.includes("@")) {
    throw new Error("Enter a valid broker email.");
  }

  const now = Math.floor(Date.now() / 1000);
  return [
    encodeJwtPart({ alg: "none", typ: "JWT" }),
    encodeJwtPart({
      sub: "broker-" + randomToken(12),
      role: "broker",
      mock: true,
      iat: now,
      exp: now + 60 * 60,
    }),
    "local-mock-signature",
  ].join(".");
}

function encodeJwtPart(value: object): string {
  return btoa(JSON.stringify(value))
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replaceAll("=", "");
}

function randomToken(length: number): string {
  const bytes = crypto.getRandomValues(new Uint8Array(length));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}
